import React, { useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { financeService } from '../../services/financeService';
import HeaderNewTabButton from '../Shared/HeaderNewTabButton';
import { COMPACT_CURRENCY_COLUMN } from '../Shared/gridColumnPresets';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '../Venues/VenuesPage.css';
import './FinancialsPage.css';
import './FinancialsReports.css';

ModuleRegistry.registerModules([AllCommunityModule]);

const ENTITIES = ['TWZ_NY', 'TWZ_NA', 'TWZ_EU', 'LONDON', 'UNASSIGNED'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'PLN', 'UNKNOWN'];
const PARTIES = ['TWZ', 'TS', 'DV', 'UNASSIGNED'];

const money = (value) => (
  value == null ? '-' : Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
);
const moneyColumn = (headerName, field) => ({
  headerName, field, width: 140, type: 'numericColumn', valueFormatter: (p) => money(p.value),
});
const unassignedClass = (p) => (p.value === 'UNASSIGNED' ? 'finance-unassigned' : '');

const PURCHASE_COLUMNS = [
  { headerName: 'Purchase ID', field: 'purchase_id', width: 120, pinned: 'left' },
  { headerName: 'Purchased', field: 'purchased_at', width: 120 },
  { headerName: 'Event', field: 'event_name', minWidth: 200, flex: 1 },
  { headerName: 'Event Date', field: 'event_date', width: 120 },
  {
    headerName: 'Event Date Source',
    field: 'event_date_source',
    width: 150,
    cellClass: (p) => (p.value === 'purchase_text' ? 'finance-unassigned' : ''),
  },
  { headerName: 'Venue', field: 'venue', minWidth: 160 },
  { headerName: 'Region', field: 'region', width: 130 },
  { headerName: 'Entity', field: 'entity', width: 120, cellClass: unassignedClass },
  { headerName: 'Platform', field: 'platform', width: 120 },
  { headerName: 'Card', field: 'card', width: 130 },
  { headerName: 'Paid by', field: 'cash_party', width: 110, cellClass: unassignedClass },
  { headerName: 'Currency', field: 'currency', ...COMPACT_CURRENCY_COLUMN },
  { headerName: 'Qty', field: 'quantity', width: 80, type: 'numericColumn' },
  { headerName: 'Units', field: 'units_total', width: 90, type: 'numericColumn' },
  { headerName: 'Units Sold', field: 'units_sold_by_cutoff', width: 110, type: 'numericColumn' },
  { headerName: 'Units Unsold', field: 'units_unsold_at_cutoff', width: 120, type: 'numericColumn' },
  moneyColumn('Cost Total', 'cost_total'),
  moneyColumn('Cost Unsold (Inventory)', 'cost_unsold_at_cutoff'),
  moneyColumn('Cost Sold', 'cost_sold_by_cutoff'),
  moneyColumn('Sales by Cutoff', 'sales_by_cutoff'),
  { headerName: 'Status', field: 'status', width: 120 },
  {
    headerName: 'Flags',
    field: 'flags',
    minWidth: 220,
    valueFormatter: (p) => (p.value || []).join(', '),
    cellClass: (p) => (p.value?.length ? 'finance-unassigned' : ''),
  },
];

const UNIT_COLUMNS = [
  { headerName: 'Unit', field: 'unit_code', width: 160, pinned: 'left' },
  { headerName: 'Purchase ID', field: 'purchase_id', width: 120 },
  { headerName: 'Event', field: 'event_name', minWidth: 200, flex: 1 },
  { headerName: 'Event Date', field: 'event_date', width: 120 },
  { headerName: 'Entity', field: 'entity', width: 120, cellClass: unassignedClass },
  { headerName: 'Currency', field: 'currency', ...COMPACT_CURRENCY_COLUMN },
  moneyColumn('Unit Cost', 'unit_cost'),
  { headerName: 'Order ID', field: 'order_id', width: 130 },
  { headerName: 'Order Date', field: 'order_date', width: 120 },
  moneyColumn('Sale Price', 'sale_price'),
  {
    headerName: 'Sold by Cutoff',
    field: 'sold_by_cutoff',
    width: 130,
    valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
  },
];

const PrepurchasedInventoryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routePrefix = location.pathname.startsWith('/dashboard') ? '/dashboard' : '/user';
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const gridRef = useRef(null);

  const purchaseYear = Number(searchParams.get('year')) || new Date().getFullYear();
  const cutoffDate = searchParams.get('cutoff') || `${purchaseYear}-12-31`;
  const eventYear = searchParams.get('event_year') || '';
  const entity = searchParams.get('entity') || '';
  const currency = searchParams.get('currency') || '';
  const cashParty = searchParams.get('cash_party') || '';
  const detail = searchParams.get('detail') === 'unit' ? 'unit' : 'purchase';
  const missingOnly = searchParams.get('missing') === '1';

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value == null || value === false) next.delete(key);
    else next.set(key, value === true ? '1' : String(value));
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    // Wait until a full year is typed (e.g. not "20" on the way to "2026").
    if (purchaseYear < 2000 || purchaseYear > 2100 || (eventYear && (eventYear < 2000 || eventYear > 2100))) {
      return undefined;
    }
    let isCurrent = true;
    setLoading(true);
    setError('');
    financeService.getPrepurchasedInventory({
      purchase_year: purchaseYear,
      cutoff_date: cutoffDate,
      event_year: eventYear || undefined,
      entity: entity || undefined,
      currency: currency || undefined,
      cash_party: cashParty || undefined,
      detail,
      missing_purchase_date: missingOnly || undefined,
    }).then((response) => {
      if (!isCurrent) return;
      if (response.success) setReport(response);
      else {
        setReport(null);
        setError(response.error);
      }
      setLoading(false);
    });
    return () => { isCurrent = false; };
  }, [purchaseYear, cutoffDate, eventYear, entity, currency, cashParty, detail, missingOnly]);

  const exportCsv = () => {
    gridRef.current?.api?.exportDataAsCsv({
      fileName: `tikwiz-prepurchased-inventory-${purchaseYear}-cutoff-${cutoffDate.replace(/-/g, '')}`
        + `${detail === 'unit' ? '-units' : ''}.csv`,
    });
  };

  const select = (label, value, key, values) => (
    <label className="finance-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => setParam(key, event.target.value)}>
        <option value="">All</option>
        {values.map((item) => <option key={item} value={item}>{item}</option>)}
      </select>
    </label>
  );

  return (
    <div className="financials-page">
      <header className="financials-header">
        <div className="financials-header-content">
          <HeaderNewTabButton />
          <div>
            <h1>Prepurchased Inventory</h1>
          </div>
          <div className="financials-header-actions">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => navigate(`${routePrefix}/financials/reports`)}
            >
              Back to Reports
            </button>
          </div>
        </div>
      </header>

      <main className="financials-main">
        <p className="finance-hint">
          Purchases made in {purchaseYear} for events after {cutoffDate}. &quot;Cost Unsold&quot; is the year-end inventory
          figure; sold-before-cutoff numbers are shown separately for the accountant.
        </p>

        <div className="finance-toolbar">
          <label className="finance-field">
            <span>Purchase year</span>
            <input
              type="number"
              min="2000"
              max="2100"
              value={purchaseYear}
              onChange={(event) => {
                const next = new URLSearchParams(searchParams);
                next.set('year', event.target.value);
                next.delete('cutoff');
                setSearchParams(next, { replace: true });
              }}
            />
          </label>
          <label className="finance-field">
            <span>Cutoff date</span>
            <input type="date" value={cutoffDate} onChange={(event) => setParam('cutoff', event.target.value)} />
          </label>
          <label className="finance-field">
            <span>Event year</span>
            <input
              type="number"
              min="2000"
              max="2100"
              placeholder="Any after cutoff"
              value={eventYear}
              onChange={(event) => setParam('event_year', event.target.value)}
            />
          </label>
          {select('Entity', entity, 'entity', ENTITIES)}
          {select('Currency', currency, 'currency', CURRENCIES)}
          {select('Paid by', cashParty, 'cash_party', PARTIES)}
          <div className="finance-toolbar-actions">
            <button
              className={`btn ${detail === 'unit' ? 'btn-primary' : 'btn-secondary'}`}
              type="button"
              onClick={() => setParam('detail', detail === 'unit' ? '' : 'unit')}
            >
              Unit detail
            </button>
            <button className="btn btn-secondary" type="button" disabled={!report?.items?.length} onClick={exportCsv}>
              Export CSV
            </button>
          </div>
        </div>

        {error && <div className="notification error">{error}</div>}

        {report && (
          <>
            <div className="finance-summary-strip">
              {report.summary.map((item) => (
                <div className="finance-summary-card" key={`${item.entity}-${item.currency}`}>
                  <strong>{item.entity} · {item.currency}</strong>
                  <span>{item.purchases} purchases · {item.units} units</span>
                  <span>Cost total: {money(item.cost_total)}</span>
                  <span className="finance-summary-main">Cost unsold: {money(item.cost_unsold_at_cutoff)}</span>
                  <span>Cost sold: {money(item.cost_sold_by_cutoff)}</span>
                  <span>Sales by cutoff: {money(item.sales_by_cutoff)}</span>
                </div>
              ))}
              {!report.summary.length && !missingOnly && <p className="finance-empty">No purchases match these filters.</p>}
            </div>

            {(report.missing_purchase_date_count > 0 || missingOnly) && (
              <div className="notification finance-warning">
                {report.missing_purchase_date_count} purchase(s) have no purchase date, so they can&apos;t be placed in a year.
                <button className="btn btn-secondary finance-inline-btn" type="button" onClick={() => setParam('missing', !missingOnly)}>
                  {missingOnly ? 'Back to report' : 'Show them'}
                </button>
              </div>
            )}
            {report.unknown_event_date_count > 0 && (
              <div className="notification finance-warning">
                {report.unknown_event_date_count} purchase(s) have no event date and are not included.
              </div>
            )}
          </>
        )}

        {loading ? (
          <div className="financials-loading"><p>Loading report...</p></div>
        ) : (
          <div className="venues-grid-shell">
            <div className="ag-theme-quartz-dark venues-ag-grid finance-ag-grid">
              <AgGridReact
                ref={gridRef}
                theme="legacy"
                rowData={report?.items || []}
                columnDefs={detail === 'unit' ? UNIT_COLUMNS : PURCHASE_COLUMNS}
                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                pagination
                paginationPageSize={100}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PrepurchasedInventoryPage;
